import { Children, useEffect, useId, useRef, useState } from "react";
import "./PumaFamily2026.css";
import PumaFamilyHero from "../components/PumaFamilyHero";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { submitPumaFamilyRequest } from "../services/pumaFamilyRequests";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Gauge } from "lucide-react";
import { DEALERSHIP_OPTIONS } from "../data/dealerships";
import { pumaFamilySchema } from "../lib/leadFormSchemas";
import { Button } from "../components/ui/button";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "../components/ui/field";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

function TrimCard({ title, children }) {
  return (
    <article className="puma-family-trim-card">
      <h3>{title}</h3>
      {children}
    </article>
  );
}

function TrimCarousel({ label, electric = false, children }) {
  const track = useRef(null);
  const trackId = useId();
  const [active, setActive] = useState(0);
  const count = Children.count(children);
  const updateActive = () => {
    const el = track.current;
    const step =
      el.children[0].getBoundingClientRect().width +
      parseFloat(getComputedStyle(el).gap);
    setActive(
      Math.max(0, Math.min(count - 1, Math.round(el.scrollLeft / step))),
    );
  };
  const move = (direction) => {
    const el = track.current;
    const step =
      el.children[0].getBoundingClientRect().width +
      parseFloat(getComputedStyle(el).gap);
    el.scrollTo({
      left: (active + direction) * step,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "instant"
        : "smooth",
    });
  };
  return (
    <section aria-label={label}>
      <div
        id={trackId}
        ref={track}
        onScroll={updateActive}
        className={`puma-family-trims ${electric ? "puma-family-trims-electric" : ""}`}
      >
        {children}
      </div>
      <div className="puma-family-trim-navigation">
        <button
          type="button"
          aria-label="Предишно ниво"
          aria-controls={trackId}
          disabled={active === 0}
          onClick={() => move(-1)}
        >
          <ChevronLeft size={22} aria-hidden="true" />
        </button>
        <span aria-live="polite" aria-atomic="true">
          {active + 1} / {count}
        </span>
        <button
          type="button"
          aria-label="Следващо ниво"
          aria-controls={trackId}
          disabled={active === count - 1}
          onClick={() => move(1)}
        >
          <ChevronRight size={22} aria-hidden="true" />
        </button>
      </div>
    </section>
  );
}

function PumaFamily2026() {
  const navigate = useNavigate();
  const offerForm = useRef(null);
  const [offerFormVisible, setOfferFormVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      setOfferFormVisible(entry.isIntersecting);
    });
    observer.observe(offerForm.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    document.body.classList.add("puma-family-page");
    return () => document.body.classList.remove("puma-family-page");
  }, []);
  const [submitError, setSubmitError] = useState("");

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(pumaFamilySchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      dealership: "",
      model: "",
    },
  });

  const marketingCampaign = async (data) => {
    setSubmitError("");
    try {
      await submitPumaFamilyRequest(data, window.location.href);
    } catch (error) {
      console.error("Грешка при запис на запитването:", error);
      setSubmitError("Запитването не беше записано. Моля, опитайте отново.");
      return;
    }

    try {
      window.gtag?.("event", "conversion", {
        send_to: "AW-17814485194/HOcACLil1d0bEMrxza5C",
      });
    } catch (error) {
      console.error("Грешка при отчитане на конверсия:", error);
    }
    navigate("/thank-you-testdrive");
  };

  const renderContactForm = () => {
    return (
      <form
        onSubmit={handleSubmit(marketingCampaign)}
        className="w-full max-w-3xl px-6 mx-auto puma-family-form sm:px-8"
        noValidate
      >
        <h2 className="text-[28px] md:text-4xl leading-tight text-center text-[#00095b]">
          Поискай оферта
        </h2>
        <FieldGroup className="gap-6 mt-10">
          <Field data-invalid={!!errors.model}>
            <FieldLabel htmlFor="puma-model">Модел *</FieldLabel>
            <Controller
              name="model"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger
                    id="puma-model"
                    onBlur={field.onBlur}
                    aria-invalid={!!errors.model}
                    aria-describedby={
                      errors.model ? "puma-model-error" : undefined
                    }
                    className="w-full h-12 px-4 text-base shadow-none rounded-xl border-slate-200 bg-slate-50"
                  >
                    <SelectValue placeholder="Изберете модел" />
                  </SelectTrigger>
                  <SelectContent className="bg-white font-fordAntennaRegular">
                    <SelectItem value="Puma">Ford Puma – мек хибрид</SelectItem>
                    <SelectItem value="Puma Gen-E">
                      Ford Puma Gen-E – електрически
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            <FieldError id="puma-model-error">
              {errors.model?.message}
            </FieldError>
          </Field>

          <Field data-invalid={!!errors.name}>
            <FieldLabel htmlFor="name">Име *</FieldLabel>
            <Input
              id="name"
              autoComplete="name"
              aria-invalid={!!errors.name}
              className="h-11 rounded-xl border-slate-200 bg-[rgb(248,250,252)] px-4 text-base shadow-none focus-visible:border-fordBlue focus-visible:ring-0"
              {...register("name")}
            />
            <FieldError>{errors.name?.message}</FieldError>
          </Field>

          <Field data-invalid={!!errors.email}>
            <FieldLabel htmlFor="email">Имейл *</FieldLabel>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              aria-invalid={!!errors.email}
              className="h-11 rounded-xl border-slate-200 bg-[rgb(248,250,252)] px-4 text-base shadow-none focus-visible:border-fordBlue focus-visible:ring-0"
              {...register("email")}
            />
            <FieldError>{errors.email?.message}</FieldError>
          </Field>

          <Field data-invalid={!!errors.phone}>
            <FieldLabel htmlFor="phone">Телефон *</FieldLabel>
            <Input
              id="phone"
              type="tel"
              autoComplete="tel"
              aria-invalid={!!errors.phone}
              className="h-11 rounded-xl border-slate-200 bg-[rgb(248,250,252)] px-4 text-base shadow-none focus-visible:border-fordBlue focus-visible:ring-0"
              {...register("phone")}
            />
            <FieldError>{errors.phone?.message}</FieldError>
          </Field>

          <Field data-invalid={!!errors.dealership}>
            <FieldLabel htmlFor="dealership">
              Предпочитано представителство *
            </FieldLabel>
            <Controller
              name="dealership"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger
                    id="dealership"
                    onBlur={field.onBlur}
                    className="h-12 w-full rounded-xl border-slate-200 bg-[rgb(248,250,252)] px-4 text-base shadow-none focus-visible:border-fordBlue focus-visible:ring-0"
                    aria-invalid={!!errors.dealership}
                  >
                    <SelectValue placeholder="Моля, изберете" />
                  </SelectTrigger>
                  <SelectContent className="bg-white font-fordAntennaRegular">
                    {DEALERSHIP_OPTIONS.map((dealership) => (
                      <SelectItem
                        key={dealership.email}
                        value={dealership.email}
                      >
                        {dealership.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            <FieldError>{errors.dealership?.message}</FieldError>
          </Field>
        </FieldGroup>

        {submitError && (
          <p role="alert" className="mt-4 text-red-600">
            {submitError}
          </p>
        )}
        <div className="flex justify-center pt-8">
          <Button
            type="submit"
            size="lg"
            disabled={isSubmitting}
            className="puma-family-cta px-10 py-3 text-lg font-normal rounded-full min-h-[48px] h-auto"
          >
            {isSubmitting ? "Изпращане..." : "Изпрати"}
          </Button>
        </div>
      </form>
    );
  };

  return (
    <div className="font-fordAntennaRegular scroll-smooth">
      {!offerFormVisible && (
        <a
          href="#poiskayOferta"
          className="puma-family-cta puma-family-floating-offer"
          onClick={(event) => {
            event.preventDefault();
            offerForm.current?.scrollIntoView({
              behavior: window.matchMedia("(prefers-reduced-motion: reduce)")
                .matches
                ? "auto"
                : "smooth",
              block: "start",
            });
          }}
        >
          Поискай оферта
        </a>
      )}
      <PumaFamilyHero />
      <div className="puma-family-content mx-auto w-full max-w-[1440px]">
        <section className="mx-auto max-w-[960px] px-6 pt-14 text-left text-[#00095b] md:px-10 md:pt-20 md:text-center">
          <h2 className="puma-family-intro-title text-[28px] leading-tight md:text-4xl md:leading-[1.25]">
            Новият Ford Puma<sup className="text-sm align-super">®</sup> –
            създаден за града
          </h2>
          <p className="font-fordAntennaRegular font-normal mx-auto mt-6 max-w-[820px] text-lg leading-[1.65]">
            Ford Puma е компактен SUV с отличителен дизайн, динамично шофиране,
            усъвършенствани технологии и впечатляваща практичност. Изберете
            между Ford Puma с хибридно задвижване и изцяло електрическия Puma
            Gen-E — създадени за динамично и уверено движение в града.
          </p>
        </section>
        <div className="flex justify-center px-6 mt-8 text-center md:mt-10">
          <button
            className="puma-family-cta font-fordAntennaRegular font-normal min-h-[48px] px-10 py-3 text-lg leading-6 border rounded-full"
            onClick={() => {
              const ofertaSection = document.getElementById("poiskayOferta");
              if (ofertaSection) {
                ofertaSection.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              }
            }}
          >
            Поискай оферта
          </button>
        </div>
        <h2 className="puma-family-model-title">
          Ford Puma с мека хибридна технология
        </h2>
        <section
          className="puma-family-powertrain"
          aria-label="Двигател и трансмисия на Ford Puma"
        >
          <article className="puma-family-spec-card">
            <Gauge
              className="puma-family-spec-icon"
              strokeWidth={1.5}
              aria-hidden="true"
            />
            <p>
              Ford Puma се предлага с бензинови EcoBoost двигатели с мека
              хибридна технология и мощност от 125 к.с., 155 к.с. или 160 к.с.
            </p>
          </article>
          <article className="puma-family-spec-card">
            <svg
              className="puma-family-spec-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M5 7v10M12 7v10M19 7v5H5" />
              <circle cx="5" cy="5" r="2" />
              <circle cx="12" cy="5" r="2" />
              <circle cx="19" cy="5" r="2" />
              <circle cx="5" cy="19" r="2" />
              <circle cx="12" cy="19" r="2" />
            </svg>
            <p>
              Изберете между 6-степенна механична и 7-степенна автоматична
              трансмисия, за да се насладите на динамично и уверено шофиране,
              съобразено с вашия стил.
            </p>
          </article>
        </section>
        <div className="relative mt-10 overflow-hidden rounded-xl">
          <img
            src="/images/puma2024/2024_FORD_PUMA_25-LOW.jpg"
            alt=""
            className="w-full rounded-xl"
          />
          <ul className="puma-family-highlights puma-family-highlights-hybrid">
            <li className="mt-4">
              <span className="font-bold">Мощен и икономичен</span> – с последно
              поколение мека хибридна технология за по-нисък разход на гориво.
            </li>
            <li className="mt-4">
              <span className="font-bold">Смарт функции</span> – оборудван с
              най-новите технологии за свързаност и в помощ на в водача,
              включително адаптивен круиз контрол и 360-градусова камера.
            </li>
            <li className="mt-4">
              <span className="font-bold">Сигурен и безопасен</span> – Оборудван
              с най-усъвършенстваните технологии за сигурлност и безопасност,
              включително Асистент за предотвратяване на сблъсък и aвтоматично
              задействане на спирачките след инцидент; Асистент за следене на
              пътната лента; система за следене умората у водача и показателите
              на шофиране.
            </li>
            <li className="mt-4">
              <span className="font-bold">Стилен и практичен</span> – с гъвкаво
              багажно пространство от 456 литра и просторен и стилен интериор,
              който отговаря на всеки стил.
            </li>
          </ul>
        </div>
        <TrimCarousel label="Нива на оборудване на Ford Puma">
          <TrimCard title="Titanium">
            <p className="puma-family-trim-description">
              За тези, които предпочитат по-комфортно шофиране
            </p>
            <ul>
              <li>17" черни алуминиеви джанти</li>
              <li>
                LED предни фарове и LED дневни светлини, LED задни светлини
              </li>
              <li>
                12" сензорен екран с подобрена инфоразвлекателна система SYNC 4,
                Apple CarPlay/Android Auto, задни паркинг сензори.
              </li>
            </ul>
          </TrimCard>
          <TrimCard title="ST-LINE">
            <p className="puma-family-trim-description">
              За тези, които търсят спортна визия и по-енергично изживяване на
              пътя
            </p>
            <ul>
              <li>17" алуминиеви джанти ST-Line дизайн</li>
              <li>Спортно окачване за по-динамично управление</li>
              <li>
                Уникален интериор с черна тапицерия на седалките с червени
                шевове
              </li>
            </ul>
          </TrimCard>
          <TrimCard title="ST-LINE X">
            <p className="puma-family-trim-description">
              Комбинира спортен стил с комфорт и първокласни технологии
            </p>
            <ul>
              <li>18" алуминиеви джанти </li>
              <li>Тапицерия на седалките от кожа/велур с червени шевове</li>
              <li>Премиум аудиосистема B&O с 10 високоговорителя</li>
              <li>Електрическа врата на багажника</li>
            </ul>
          </TrimCard>
          <TrimCard title="ST">
            <p className="puma-family-trim-description">
              За търсещите адреналин на пътя и вълнуващо изживяване
            </p>
            <ul>
              <li>19" алуминиеви джанти</li>
              <li>360° обзор над автомобила</li>
              <li>Адаптивен автопилот</li>
              <li>Отопляеми предни седалки, челно стъкло и волан</li>
            </ul>
          </TrimCard>
        </TrimCarousel>
        <h2 className="puma-family-model-title">
          Изцяло електрическият Ford Puma Gen-E<sup>®</sup>
        </h2>
        <p className="mx-auto mb-10 max-w-[820px] px-6 text-lg leading-[1.65] text-[#00095b] md:text-center">
          Новият изцяло електрически Ford Puma® съчетава динамично управление,
          интелигентни технологии и впечатляваща практичност в компактен SUV
          формат.
        </p>
        <div className="relative mt-4 overflow-hidden rounded-xl">
          <img
            src="/images/puma-family-2026/puma-gen-e.jpg"
            alt="Изцяло електрическият Ford Puma Gen-E"
            className="w-full"
          />
          <ul className="puma-family-highlights puma-family-highlights-electric">
            <li>
              <h3 className="mb-3 text-2xl">До 419 км пробег</h3>
              <p>
                Изминавайте повече с едно зареждане и се наслаждавайте на
                свободата да пътувате без излишни спирки.
              </p>
            </li>
            <li>
              <h3 className="mb-3 text-2xl">
                До 135 км пробег за 10 минути зареждане
              </h3>
              <p>
                С бързото зареждане можете да добавите до 135 км пробег само за
                10 минути – идеално за динамичното ежедневие.
              </p>
            </li>
            <li>
              <h3 className="mb-3 text-2xl">Ефективност</h3>
              <p>
                13,1 kWh/100 км комбиниран разход на енергия – за ефективно и
                икономично електрическо шофиране.
              </p>
            </li>
          </ul>
        </div>
        <TrimCarousel label="Нива на оборудване на Ford Puma Gen-E" electric>
          <TrimCard title="Select">
            <ul>
              <li>17-инчови стоманени джанти с гуми 215/55 R17 94H</li>
              <li>
                LED фарове с автоматично включване/изключване на дългите
                светлини
              </li>
              <li>Gigabox и преден багажник (frunk)</li>
              <li>Текстилни седалки</li>
              <li>
                12-инчов хоризонтален сензорен дисплей с безжични Apple CarPlay
                и Android Auto, аудиосистема с 6 високоговорителя, Ford SYNC със
                свързана навигация,
              </li>
              <li>
                Напълно цифрово 12.8" инструментално табло и скоростен лост на
                кормилната колона
              </li>
              <li>Многоцветно амбиентно осветление</li>
              <li>Безжично зарядно устройство</li>
              <li>Камера за заден ход</li>
            </ul>
          </TrimCard>
          <TrimCard title="Premium">
            <ul>
              <li>18-инчови алуминиеви джанти с гуми 215/50 R18 92H</li>
              <li>
                LED фарове с автоматично включване/изключване на дългите
                светлини
              </li>
              <li>Gigabox и преден багажник (frunk)</li>
              <li>Sensico седалки</li>
              <li>
                12-инчов сензорен екран с SYNC4 със свързана навигация,
                аудиосистема B&O с 10 високоговорителя (включително субуфер)
              </li>
              <li>
                Напълно цифрово 12.8" инструментално табло и скоростен лост на
                кормилната колона
              </li>
              <li>Многоцветно амбиентно осветление</li>
              <li>Безжично зарядно устройство</li>
              <li>Камера за заден ход</li>
            </ul>
          </TrimCard>
          <TrimCard title="Black Edition">
            <ul>
              <li>18" Алуминиеви джанти с покритие Magnetite</li>
              <li>Син цвят на купето Azura Blue</li>
              <li>Контрастен черен покрив и черни външни огледала</li>
              <li>
                Матрични LED фарове против заслепяване с автоматични дълги
                светлини
              </li>
              <li>
                Black Edition: седалки от черна еко-кожа Sensico и велур Neo
                Suede със син шев
              </li>
              <li>
                12-инчов хоризонтален сензорен дисплей с безжични Apple CarPlay
                и Android Auto, аудиосистема с 6 високоговорителя, Ford SYNC със
                свързана навигация,
              </li>
              <li>Предни и задни паркинг сензори</li>
              <li>Многоцветно амбиентно осветление</li>
              <li>
                Напълно цифрово 12.8" инструментално табло и скоростен лост на
                кормилната колона
              </li>
              <li>Затъмнени задни стъкла (за поверителност)</li>
              <li>Gigabox и преден багажник (frunk)</li>
            </ul>
          </TrimCard>
        </TrimCarousel>
        <section
          className="puma-family-summary"
          aria-labelledby="puma-family-summary-title"
        >
          <h2 id="puma-family-summary-title">Защо да изберете Puma</h2>
          <ul className="puma-family-summary-benefits">
            <li>
              <h3>
                Интелигентен дизайн
                <br />и практичност
              </h3>
              <p>
                Мултифункционални и удобни автомобили, създадени да се впишат в
                ежедневието ви.
              </p>
            </li>
            <li>
              <h3>
                Сигурност
                <br />и спокойствие
              </h3>
              <p>
                Технологии за сигурност и безопасност, които ви помагат да
                шофирате уверено.
              </p>
            </li>
            <li>
              <h3>
                Създадени
                <br />
                за града
              </h3>
              <p>
                Компактни, но просторни, с възможности за лесно маневриране и
                паркиране.
              </p>
            </li>
          </ul>
        </section>
        <section
          className="flex flex-col items-center justify-center gap-6 px-6 py-12 md:flex-row md:py-16"
          aria-label="Налични автомобили"
        >
          <a
            className="puma-family-cta inline-flex w-[260px] max-w-full min-h-[48px] items-center justify-center rounded-full px-4 py-3 text-center text-base whitespace-nowrap"
            href="https://sale.ford.bg/?model=Puma"
            target="_blank"
            rel="noopener noreferrer"
          >
            Налични Puma
          </a>
          <a
            className="puma-family-cta inline-flex w-[260px] max-w-full min-h-[48px] items-center justify-center rounded-full px-4 py-3 text-center text-base whitespace-nowrap"
            href="https://sale.ford.bg/?model=Puma+Gen-E"
            target="_blank"
            rel="noopener noreferrer"
          >
            Налични Puma Gen-E
          </a>
        </section>

        <motion.section
          ref={offerForm}
          id="poiskayOferta"
          className="w-full mx-auto puma-family-form-section"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.5, delay: 1.3 }}
        >
          {renderContactForm()}
        </motion.section>

        <section className="max-w-4xl mx-auto mt-12 mb-16">
          <p className="px-6 text-sm leading-relaxed text-slate-500">
            * Специалната цена от 20 134,81 евро с ДДС е валидна за Ford Puma
            Titanium с 1,0-литров бензинов двигател EcoBoost, 125 к.с., при
            покупка с еднократно заплащане на пълната продажна цена, без
            финансиране. Стандартната цена на автомобила е 23 970 евро с ДДС.
            <br />
            **Първоначалният разход се отнася за същия модел и при 60-месечен
            лизинг. Сумата включва приложимите такси, застрахователни премии и
            разходи за въвеждане на автомобила в експлоатация. Финансирането
            подлежи на одобрение от финансова институция. Офертата е валидна за
            налични автомобили до изчерпване на количествата. <br />
            Изображенията са илюстративни.
          </p>
        </section>
      </div>
      {/* {showModal && zapitvane && (
        <Modal handleClose={handleClose}>
          <GetInTouch model={model} copy={copy} />
        </Modal>
      )} */}
    </div>
  );
}

export default PumaFamily2026;
